/**
 * Voice AI — Speech Provider Service (STT / TTS)
 * Owns all speech provider API calls. Components call this service
 * instead of making direct fetch calls.
 */

import { authService } from './auth-service.js';

const API_BASE = '/voice/php/api';

class SpeechService {

  /* ─── STT API Calls ─── */

  async listSttProviders() {
    const res = await authService.apiFetch(`${API_BASE}/speech-providers.php?type=stt`);
    return res.json();
  }

  async createSttProvider(data) {
    const res = await authService.apiFetch(`${API_BASE}/speech-providers.php?type=stt`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  async updateSttProvider(key, data) {
    const res = await authService.apiFetch(`${API_BASE}/speech-providers.php?type=stt&key=${encodeURIComponent(key)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  async deleteSttProvider(key) {
    const res = await authService.apiFetch(`${API_BASE}/speech-providers.php?type=stt&key=${encodeURIComponent(key)}`, {
      method: 'DELETE'
    });
    return res.json();
  }

  async setSttDefault(key) {
    const res = await authService.apiFetch(`${API_BASE}/speech-providers.php?type=stt`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key })
    });
    return res.json();
  }

  /* ─── TTS API Calls ─── */

  async listTtsProviders() {
    const res = await authService.apiFetch(`${API_BASE}/speech-providers.php?type=tts`);
    return res.json();
  }

  async createTtsProvider(data) {
    const res = await authService.apiFetch(`${API_BASE}/speech-providers.php?type=tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  async updateTtsProvider(key, data) {
    const res = await authService.apiFetch(`${API_BASE}/speech-providers.php?type=tts&key=${encodeURIComponent(key)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  async deleteTtsProvider(key) {
    const res = await authService.apiFetch(`${API_BASE}/speech-providers.php?type=tts&key=${encodeURIComponent(key)}`, {
      method: 'DELETE'
    });
    return res.json();
  }

  async setTtsDefault(key) {
    const res = await authService.apiFetch(`${API_BASE}/speech-providers.php?type=tts`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key })
    });
    return res.json();
  }

  /* ─── STT Provider Definitions ─── */

  getSttProviderDefinitions() {
    return [
      {
        id: 'openai',
        name: 'OpenAI',
        icon: 'fa-microchip-ai',
        models: ['gpt-4o-mini-transcribe', 'gpt-4o-transcribe', 'whisper-1'],
        placeholder: 'sk-proj-...'
      },
      {
        id: 'deepgram',
        name: 'Deepgram',
        icon: 'fa-waveform-lines',
        models: ['nova-3', 'nova-2', 'nova-2:phonecall', 'nova-2:voicemail'],
        placeholder: ''
      },
      {
        id: 'google',
        name: 'Google',
        icon: 'fa-gem',
        models: ['chirp_3', 'chirp_2', 'latest_short'],
        placeholder: 'AIza...'
      },
      {
        id: 'azure',
        name: 'Azure',
        icon: 'fa-cloud',
        models: ['speech-to-text'],
        placeholder: '',
        hasRegion: true
      },
      {
        id: 'assemblyai',
        name: 'AssemblyAI',
        icon: 'fa-diagram-project',
        models: ['universal-3-pro', 'universal-streaming'],
        placeholder: ''
      },
      {
        id: 'custom',
        name: 'Custom',
        icon: 'fa-code',
        models: [],
        placeholder: '',
        hasBaseUrl: true
      }
    ];
  }

  /* ─── TTS Provider Definitions ─── */

  getTtsProviderDefinitions() {
    return [
      {
        id: 'openai',
        name: 'OpenAI',
        icon: 'fa-microchip-ai',
        models: ['gpt-4o-mini-tts', 'tts-1', 'tts-1-hd'],
        voices: ['alloy', 'ash', 'ballad', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer', 'marin', 'cedar'],
        placeholder: 'sk-proj-...'
      },
      {
        id: 'elevenlabs',
        name: 'ElevenLabs',
        icon: 'fa-volume-high',
        models: ['eleven_flash_v2_5', 'eleven_turbo_v2_5', 'eleven_multilingual_v2'],
        voices: [],  // text input — user enters voice ID
        placeholder: ''
      },
      {
        id: 'deepgram',
        name: 'Deepgram',
        icon: 'fa-waveform-lines',
        models: ['aura-2'],
        voices: ['angus', 'arcas', 'athena', 'helios', 'hera', 'luna', 'orion', 'perseus', 'stella', 'zeus'],
        placeholder: ''
      },
      {
        id: 'google',
        name: 'Google',
        icon: 'fa-gem',
        models: ['standard', 'wavenet', 'neural2'],
        voices: [],  // text input
        placeholder: 'AIza...'
      },
      {
        id: 'azure',
        name: 'Azure',
        icon: 'fa-cloud',
        models: ['neural'],
        voices: [],  // text input — user enters full voice name like en-US-JennyNeural
        placeholder: '',
        hasRegion: true,
        defaultVoice: 'en-US-JennyNeural',
        defaultVoiceLabel: 'Jenny Neural (default)'
      },
      {
        id: 'playht',
        name: 'PlayHT',
        icon: 'fa-play',
        models: ['Play3.0-mini', 'PlayDialog'],
        voices: [],  // text input
        placeholder: ''
      },
      {
        id: 'cartesia',
        name: 'Cartesia',
        icon: 'fa-bolt-lightning',
        models: ['sonic-3', 'sonic-turbo'],
        voices: [],  // user enters voice UUID from Cartesia dashboard
        placeholder: '',
        defaultVoice: 'f9836c6e-a0bd-460e-9d3c-f7299fa60f94',
        defaultVoiceLabel: 'Coral (default)'
      },
      {
        id: 'custom',
        name: 'Custom',
        icon: 'fa-code',
        models: [],
        voices: [],
        placeholder: '',
        hasBaseUrl: true
      }
    ];
  }

  /* ─── Lookup Helpers ─── */

  getSttProviderDef(providerId) {
    return this.getSttProviderDefinitions().find(p => p.id === providerId) || null;
  }

  getTtsProviderDef(providerId) {
    return this.getTtsProviderDefinitions().find(p => p.id === providerId) || null;
  }

  getSttModelsForProvider(providerId) {
    const def = this.getSttProviderDef(providerId);
    return def ? def.models : [];
  }

  getTtsModelsForProvider(providerId) {
    const def = this.getTtsProviderDef(providerId);
    return def ? def.models : [];
  }

  getVoicesForProvider(providerId) {
    const def = this.getTtsProviderDef(providerId);
    return def ? (def.voices || []) : [];
  }
}

export const speechService = new SpeechService();
